import { Integer } from "model/modelTypes";
import React from "react";
import InputBox from "./InputBox";

export type IBoxProps = React.ComponentProps<typeof InputBox>;

type IProps = {
  valueArr: (Integer | null)[];
  setValueArr: any;
  autoFocus?: boolean;
  length: Integer;
  inputRegExp?: RegExp;
  /** Swaps in a differently styled box; the behaviour here stays the same. */
  renderBox?: (props: IBoxProps & { index: Integer }) => React.ReactNode;
  /** Renders `separator` after every `groupSize` boxes, e.g. 123 - 456. */
  groupSize?: Integer;
  separator?: React.ReactNode;
};
function CharacterInputBoxes({
  valueArr,
  setValueArr,
  autoFocus = true,
  inputRegExp = /^[0-9]$/,
  renderBox,
  groupSize,
  separator,
}: IProps) {
  const boxRefs = React.useRef<HTMLDivElement[]>([]);
  const focusTimeoutRef =
    React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const [previousValues, setPreviousValues] = React.useState<
    (Integer | null)[]
  >([]);

  React.useEffect(() => {
    if (
      valueArr.filter((v) => v === null).length === 6 &&
      previousValues.filter((v) => v === null).length === 0
    ) {
      clearTimeout(focusTimeoutRef.current);
      boxRefs.current?.[0]?.querySelector("input")?.select();
    }

    setPreviousValues(valueArr);
  }, [valueArr]);

  const handleKeyDown = ({ target, key }) => {
    if (key === "ArrowLeft" || (key === "Backspace" && target.value === "")) {
      boxRefs.current?.[parseInt(target.name) - 1]
        ?.querySelector("input")
        ?.select();
    } else if (key === "ArrowRight") {
      boxRefs.current?.[parseInt(target.name) + 1]
        ?.querySelector("input")
        ?.select();
    }
  };

  const handleChange = ({ target }) => {
    if (target.value === "" || inputRegExp.test(target.value)) {
      if (boxRefs.current?.[target.name] !== null && target.value !== "") {
        boxRefs.current?.[parseInt(target.name) + 1]
          ?.querySelector("input")
          ?.select();
      }

      setValueArr((v) => {
        const newV = [...v];
        newV[target.name] = target.value === "" ? null : target.value;
        return newV;
      });
    } else {
      target.value = valueArr[target.name];
    }
  };

  const handleFocus = ({ target }) => {
    const el = target;
    clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(function () {
      const input = boxRefs.current?.[el.name]?.querySelector("input");
      if (input && document.activeElement === input) {
        input.select();
      }
    }, 0);
  };

  return (
    <>
      {valueArr.map((v, i) => {
        const boxProps: IBoxProps = {
          name: `${i}`,
          autoFocus: autoFocus && i === 0,
          handleKeyDown,
          handleFocus,
          handleChange,
          inputProps: { value: v !== null ? v : "" },
          boxRef: (el) => {
            if (!el) return;
            boxRefs.current[i] = el;
          },
        };

        return (
          <React.Fragment key={i}>
            {groupSize && i > 0 && i % groupSize === 0 ? separator : null}
            {renderBox ? (
              renderBox({ ...boxProps, index: i })
            ) : (
              <InputBox {...boxProps} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export default CharacterInputBoxes;
